import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { readRemScale } from '@/utils/remScale';
import { cx } from './cx';
import { topOverlayElement } from './overlayStack';
import { focusableIn } from './useFocusTrap';

export interface PopoverTriggerProps {
  ref: React.Ref<HTMLButtonElement>;
  'aria-haspopup': 'dialog';
  'aria-expanded': boolean;
  'aria-controls'?: string;
  onClick: () => void;
}

export interface PopoverProps {
  trigger: (props: PopoverTriggerProps) => React.ReactElement;
  /** Accessible name of the panel (translated). */
  'aria-label': string;
  align?: 'start' | 'end';
  /** Panel width in px at scale 1 (it grows with the UI scale); defaults to 320, capped to the viewport. */
  width?: number;
  className?: string;
  /** Called when the panel opens or closes (e.g. to load content on open). */
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}

/** Anchored, non-modal panel (notifications, help): Escape and outside click close it. */
export default function Popover({ trigger, 'aria-label': ariaLabel, align = 'end', width = 320, className, onOpenChange, children }: PopoverProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; maxHeight?: number; left?: number; right?: number; width: number }>({ top: 0, width });
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = document.documentElement.clientHeight || window.innerHeight;
    // Sizes follow the rem scale (like the CSS); the 6/8/16px offsets from the trigger and edges stay px.
    const k = readRemScale();
    const w = Math.min(width * k, vw - 16);
    // Open upwards when there is little room below and more above (e.g. a picker in a phone bottom sheet).
    const below = vh - r.bottom - 6 - 8;
    const above = r.top - 6 - 8;
    const up = below < 240 * k && above > below;
    const cap = Math.min(480 * k, vh * 0.7);
    const vertical = up ? { bottom: vh - r.top + 6, maxHeight: Math.min(cap, above) } : { top: r.bottom + 6, maxHeight: Math.min(cap, below) };
    setPos(
      align === 'end'
        ? { ...vertical, right: Math.max(8, vw - r.right), width: w }
        : { ...vertical, left: Math.max(8, Math.min(r.left, vw - w - 8)), width: w }
    );
  }, [align, width]);

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    (focusableIn(panelRef.current)[0] ?? panelRef.current).focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    // Follow the trigger when a scroll container (page, dialog body) moves it.
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: () => {
          if (open) return close(false);
          place();
          setTarget(topOverlayElement() ?? document.body);
          setOpen(true);
        },
      })}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role="dialog"
              aria-label={ariaLabel}
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close(true);
                }
              }}
              style={{ top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, left: pos.left, right: pos.right, width: pos.width }}
              className={cx(
                'fixed z-popover flex max-h-[min(70dvh,30rem)] flex-col overflow-hidden rounded-container border k-panel bg-raised text-primary focus:outline-none',
                className
              )}
            >
              {typeof children === 'function' ? children(() => close(true)) : children}
            </div>,
            target ?? document.body
          )
        : null}
    </>
  );
}
